import React, { useState, useEffect } from 'react';
import ComplianceDashboard from './ComplianceDashboard';
import ClientList from './ClientList';
import ClientFolder from './ClientFolder';
import NewCase from './NewCase';
import CaseDetail from './CaseDetail';
import CaseList from './CaseList';
import { Home, Users, FileText, Plus, Settings, BarChart3, Menu, X } from 'lucide-react';

const ComplianceApp = ({ agent, isAdmin = false }) => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [pageParams, setPageParams] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigate = (page, params = {}) => {
    setCurrentPage(page);
    setPageParams(params);
    setSidebarOpen(false); // close drawer on nav (mobile)
  };

  // Close drawer on Escape (a11y) + lock scroll while open on mobile
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'cases', label: 'All Files', icon: FileText },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'new-case', label: 'New File', icon: Plus },
  ];

  if (isAdmin) {
    menuItems.push({ id: 'reports', label: 'Reports', icon: BarChart3 });
    menuItems.push({ id: 'settings', label: 'Settings', icon: Settings });
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <ComplianceDashboard agent={agent} onNavigate={navigate} isAdmin={isAdmin} />;
      case 'clients':
        return <ClientList agent={agent} onNavigate={navigate} isAdmin={isAdmin} />;
      case 'client-folder':
        return <ClientFolder agent={agent} client={pageParams.client} onNavigate={navigate} isAdmin={isAdmin} />;
      case 'cases':
        return <CaseList agent={agent} onNavigate={navigate} filterStatus={pageParams.status} isAdmin={isAdmin} />;
      case 'new-case':
        return <NewCase agent={agent} onNavigate={navigate} preselectedClientId={pageParams.clientId} isAdmin={isAdmin} />;
      case 'case-detail':
        return <CaseDetail agent={agent} caseId={pageParams.caseId} onNavigate={navigate} isAdmin={isAdmin} />;
      default:
        return <ComplianceDashboard agent={agent} onNavigate={navigate} isAdmin={isAdmin} />;
    }
  };

  const SidebarContent = (
    <>
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-[#D4AF37]">Life Insurance</h1>
          <p className="text-sm text-blue-200">Compliance File Builder</p>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 -mr-2 text-blue-200 hover:text-white"
          aria-label="Close menu"
          data-testid="compliance-sidebar-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="p-4 space-y-1">
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.id ||
                         (item.id === 'cases' && currentPage === 'case-detail');
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? 'bg-[#0077B6] text-white'
                  : 'text-blue-200 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`compliance-nav-${item.id}`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* User Info — pinned to bottom on desktop, inline on mobile */}
      <div className="lg:absolute lg:bottom-0 lg:left-0 lg:w-64 mt-auto p-4 border-t border-white/10 bg-[#002D44]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center font-bold flex-shrink-0">
            {agent?.name?.charAt(0) || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{agent?.name || 'Advisor'}</p>
            <p className="text-xs text-blue-200 truncate">{agent?.license_number || 'LLQP Licensed'}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 lg:flex overflow-x-hidden">
      {/* Mobile drawer backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer on mobile, static on lg+ */}
      <aside
        className={`bg-[#003D5C] text-white w-64 flex-shrink-0 lg:relative lg:flex lg:flex-col lg:min-h-screen
          fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="compliance-sidebar"
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {SidebarContent}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-[#003D5C] text-white flex items-center gap-3 px-4 py-3 shadow">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 hover:bg-white/10 rounded"
            aria-label="Open menu"
            data-testid="compliance-sidebar-open"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#D4AF37] font-semibold uppercase tracking-wide">Life Insurance</p>
            <p className="text-sm font-bold truncate">Compliance File Builder</p>
          </div>
        </div>

        <div className="bg-amber-50 border-b border-amber-200 px-3 sm:px-6 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="quebec-banner">
          <span className="font-semibold flex-shrink-0">Note:</span>
          <span>This Compliance File Builder is for use across all Canadian provinces and territories <b>except Quebec</b> (separate AMF requirements apply).</span>
        </div>
        <div className="p-3 sm:p-6">
          {renderPage()}
        </div>
      </div>
    </div>
  );
};

export default ComplianceApp;
